import { coordinateFormats } from '@gmn-portal/backend';
import { MapPinned } from 'lucide-react';
import { toast } from 'sonner';
import { useUpdateUser } from '@/core/api';
import { useAuth } from '@/core/auth';
import { useIntl } from '@/core/i18n/use-intl';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui';
import { getPreferredCoordinateFormat } from '@/shared/utils';

export function CoordinateFormatSwitcher() {
  const { t } = useIntl();
  const { user, updateUser } = useAuth();
  const updateUserMutation = useUpdateUser();

  if (!user) return null;

  const currentFormat = getPreferredCoordinateFormat(user.coordinateFormatPreference);

  const handleCoordinateFormatChange = async (
    coordinateFormatPreference: (typeof coordinateFormats)[number]
  ) => {
    if (coordinateFormatPreference === currentFormat) return;

    try {
      const newUser = await updateUserMutation.mutateAsync({
        id: user.id,
        data: { coordinateFormatPreference },
      });
      updateUser(newUser);
    } catch (_error) {
      toast.error(t('errors.unknownError'));
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`${t('settings.coordinateFormat')}: ${t(`settings.coordinateFormats.${currentFormat}`)}`}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <MapPinned className="size-[18px]" />
          <span className="flex-1 truncate text-left">
            {t(`settings.coordinateFormats.${currentFormat}`)}
          </span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top">
        {coordinateFormats.map((value) => (
          <DropdownMenuItem
            key={value}
            onClick={() => handleCoordinateFormatChange(value)}
            className={currentFormat === value ? 'bg-accent' : ''}
          >
            {t(`settings.coordinateFormats.${value}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
